import React, { memo, useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import dayjs from "dayjs";
import { Skeleton } from "@mui/material";
import { monthlyReportObjFormate } from "../../../../utils/chartKeyFormat";

const SOFT_PALETTE = {
  blue: "#4F7CFF",
  teal: "#24C8A5",
  orange: "#F5A524",
  cyan: "#2AA7E0",
  text: "#0F172A",
  muted: "#475569",
  grid: "rgba(148,163,184,0.15)",
};

const fontFamily = '"Gilroy Regular", "Gilroy", Inter, "Segoe UI", sans-serif';

const toRgba = (color, alpha = 0.18) => {
  if (!color || typeof color !== "string") return `rgba(79, 124, 255, ${alpha})`;
  const hex = color.replace("#", "");
  if (hex.length !== 6) return color;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  if ([r, g, b].some((value) => Number.isNaN(value))) return color;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatNumber = (value) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Math.abs(Number(value) || 0));

const createVerticalGradient = (chart, baseColor) => {
  const { ctx, chartArea } = chart;
  if (!chartArea) return baseColor;
  const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
  gradient.addColorStop(0, toRgba(baseColor, 0.72));
  gradient.addColorStop(1, toRgba(baseColor, 0.98));
  return gradient;
};

const BarChart = ({
  labels,
  mainData,
  chartDataSet,
  loadingState,
  filterOnchange,
  vendorDashboard,
  customCardClass,
  durationValue,
  onCardClick,
  inline,
  chartHeight: chartHeightProp,
}) => {
  const [selectedOption, setSelectedOption] = useState(durationValue || "last six months");
  const [formattedLabels, setFormattedLabels] = useState([]);
  const [tooltip, setTooltip] = useState(null);

  const axisTickFont = {
    size: 14,
    family: fontFamily,
    weight: "400",
  };

  const safeMax = mainData ? Math.max(...mainData.map(Number)) : 0;
  const yStep = safeMax > 0 ? Math.ceil(safeMax / 4) : 1;

  const scales = {
    x: {
      grid: { display: true, color: SOFT_PALETTE.grid, drawBorder: false },
      border: { display: false },
      ticks: { color: SOFT_PALETTE.muted, font: axisTickFont, padding: 10 },
    },
    y: {
      grid: { display: true, color: SOFT_PALETTE.grid, drawBorder: false },
      ticks: {
        stepSize: yStep,
        beginAtZero: true,
        color: SOFT_PALETTE.muted,
        font: axisTickFont,
        padding: 12,
        callback: (v) => v.toLocaleString("en-IN"),
      },
    },
  };

  useEffect(() => {
    const endDate = new Date();
    let nl = [];
    switch (selectedOption) {
      case "monthly":
        nl = monthlyReportObjFormate(labels)?.reverse();
        break;
      case "this week":
        nl = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        break;
      case "this month":
        nl = Array.from({ length: Math.ceil(endDate.getDate() / 7) }, (_, i) => `Week ${i + 1}`);
        break;
      case "last month":
        nl = ["Week 1", "Week 2", "Week 3", "Week 4"];
        break;
      case "last six months":
        nl = Array.from({ length: 6 }, (_, i) => dayjs().subtract(i, "month").format("MMM")).reverse();
        break;
      default:
        break;
    }
    setFormattedLabels(nl);
  }, [selectedOption, labels]);

  const dataSet = {
    labels: vendorDashboard ? formattedLabels : labels,
    datasets: [
      {
        label: chartDataSet?.label,
        backgroundColor: (context) =>
          createVerticalGradient(
            context.chart,
            Array.isArray(chartDataSet?.barColors) ? chartDataSet.barColors[0] : chartDataSet?.barColors || SOFT_PALETTE.blue,
          ),
        borderColor: Array.isArray(chartDataSet?.barColors) ? chartDataSet.barColors[0] : chartDataSet?.barColors || SOFT_PALETTE.blue,
        borderWidth: 0,
        barPercentage: 0.7,
        categoryPercentage: 0.78,
        borderRadius: 12,
        borderSkipped: false,
        data: mainData || [],
      },
    ],
  };

  const chartHeight = chartHeightProp || 380;

  const options = {
    animation: {
      duration: 400,
      easing: "easeInOutCubic",
    },
    plugins: {
      legend: false,
      tooltip: {
        enabled: false,
        external: (context) => {
          const { chart, tooltip: model } = context;
          if (!chart) return;
          if (!model || model.opacity === 0) {
            setTooltip((prev) => (prev ? { ...prev, visible: false } : prev));
            return;
          }
          const point = model.dataPoints?.[0];
          if (!point) return;
          const rect = chart.canvas.getBoundingClientRect();
          const value = formatNumber(point.parsed?.y ?? point.raw ?? 0);
          setTooltip({
            visible: true,
            x: rect.left + model.caretX,
            y: rect.top + model.caretY,
            title: chartDataSet?.chartLabels?.[0] || chartDataSet?.cardHeading || "",
            value,
          });
        },
        backgroundColor: "#FFFFFF",
        titleColor: "#0F172A",
        bodyColor: "#0F172A",
        borderColor: "rgba(226,232,240,0.9)",
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
        displayColors: false,
        titleFont: { size: 14, family: fontFamily, weight: "400" },
        bodyFont: { size: 14, family: fontFamily, weight: "400" },
      },
    },
    scales,
    maintainAspectRatio: false,
  };

  const heading = (
    <div style={{ marginBottom: "0.5rem" }}>
      <div
        style={{
          fontSize: "18px",
          fontFamily: fontFamily,
          color: "#0f172a",
          marginBottom: "0.35rem",
          fontWeight: 600,
        }}
      >
        {chartDataSet?.cardHeading}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
        {chartDataSet?.chartIndicators?.map((e) => (
          <div key={e.value} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: e.color, display: "inline-block" }} />
            <span style={{ fontSize: "13px", color: SOFT_PALETTE.muted, fontFamily: fontFamily, fontWeight: 400 }}>{e.value}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const tooltipStyle = tooltip?.visible
    ? { opacity: 1, transform: "translateY(-6px)" }
    : { opacity: 0, transform: "translateY(0px)" };

  const chartInner = (
    <div style={{ cursor: onCardClick ? "pointer" : "inherit", width: "100%", position: "relative" }} onClick={onCardClick}>
      <div style={{ width: "100%", height: chartHeight, marginTop: 4 }}>
        <Bar data={dataSet} options={options} />
      </div>
      {tooltip?.visible ? (
        <div
          style={{
            position: "fixed",
            left: tooltip.x + 14,
            top: tooltip.y - 12,
            pointerEvents: "none",
            zIndex: 60,
            minWidth: 180,
            maxWidth: 320,
            background: "#FFFFFF",
            border: "1px solid rgba(226,232,240,0.9)",
            borderRadius: 12,
            boxShadow: "0px 12px 30px rgba(15,23,42,0.12)",
            padding: "10px 12px",
            color: SOFT_PALETTE.text,
            fontFamily: fontFamily,
            fontSize: 14,
            lineHeight: 1.5,
            opacity: tooltipStyle.opacity,
            transform: tooltipStyle.transform,
            transition: "opacity 180ms ease, transform 180ms ease",
          }}
        >
          <div style={{ color: SOFT_PALETTE.muted, marginBottom: 4 }}>{tooltip.title}</div>
          <div style={{ fontWeight: 400, color: SOFT_PALETTE.text }}>{tooltip.value}</div>
        </div>
      ) : null}
    </div>
  );

  const skeleton = (
    <>
      <Skeleton width="180px" height="24px" style={{ marginBottom: 10 }} />
      <Skeleton width="100%" height={Math.max(240, chartHeight)} variant="rectangular" style={{ borderRadius: 16, backgroundColor: "rgba(226,232,240,0.6)" }} />
    </>
  );

  if (inline) {
    return <div onClick={onCardClick} style={{ cursor: onCardClick ? "pointer" : "inherit", width: "100%" }}>{loadingState ? skeleton : <>{heading}{chartInner}</>}</div>;
  }

  return (
    <div
      className={customCardClass}
      onClick={onCardClick}
      style={{
        background: "#fff",
        borderRadius: "18px",
        border: "1px solid rgba(226,232,240,0.7)",
        boxShadow: "0px 8px 30px rgba(15,23,42,0.06)",
        padding: "20px",
        minHeight: "420px",
        cursor: onCardClick ? "pointer" : "default",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {loadingState ? skeleton : <>{heading}{chartInner}</>}
    </div>
  );
};

export default memo(BarChart);
